/**
 * Shrinks a photo in the browser before upload: longest side at most
 * `maxSide` px, re-encoded as JPEG - the format WhatsApp accepts everywhere,
 * so a phone's 4-8 MB photo (or a WebP/GIF the browser can decode) becomes a
 * ~100-300 KB JPEG. Images stored in our own database stay small this way.
 *
 * A JPEG/PNG that is already small enough is uploaded untouched (keeps PNG
 * transparency, avoids recompressing). Throws a readable error when the
 * browser can't decode the file (e.g. HEIC outside Safari).
 */

const MAX_UNTOUCHED_BYTES = 400 * 1024;

async function decode(file: File): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === 'function') {
    try {
      return await createImageBitmap(file);
    } catch {
      // Fall through to <img> decoding (older Safari).
    }
  }
  const url = URL.createObjectURL(file);
  try {
    const img = new Image();
    img.src = url;
    await img.decode();
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
}

export async function resizeImageForUpload(file: File, maxSide = 1280): Promise<Blob> {
  let source: ImageBitmap | HTMLImageElement;
  try {
    source = await decode(file);
  } catch {
    throw new Error('This image could not be read. Please use a JPG or PNG photo.');
  }
  const width = 'naturalWidth' in source ? source.naturalWidth : source.width;
  const height = 'naturalHeight' in source ? source.naturalHeight : source.height;
  const scale = Math.min(1, maxSide / Math.max(width, height));

  const alreadyFine =
    scale === 1 &&
    (file.type === 'image/jpeg' || file.type === 'image/png') &&
    file.size <= MAX_UNTOUCHED_BYTES;
  if (alreadyFine) return file;

  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('This browser cannot process images.');
  // JPEG has no transparency - paint white behind transparent PNG areas.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(source, 0, 0, canvas.width, canvas.height);
  if ('close' in source) source.close();

  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, 'image/jpeg', 0.85),
  );
  if (!blob) throw new Error('This image could not be processed.');
  return blob;
}
